/**
 * Networks Data Hooks
 * React Query hooks for fetching and mutating network data
 */

import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from '../lib/date-utils';
import type {
  PlatformProvider,
  AffiliateNetwork,
  NetworkCardData,
  NewNetworkData,
  TestConnectionResult,
  NetworkFilters,
  NetworkSummary
} from '../types/networks.types';

// API configuration
const API_BASE = '/api';

// Normalize platform provider names to consistent casing
// NOTE: This must stay in sync with functions/api/networks/index.ts
const PLATFORM_NAME_MAP: Record<string, string> = {
  'netrefer': 'NetRefer',
  'myaffiliates': 'MyAffiliates',
  'incomeaccess': 'IncomeAccess',
  'income access': 'IncomeAccess',
  'referon': 'ReferOn',
  'cellxpert': 'Cellxpert',
  'softswiss': 'Affilka',  // SOFTSWISS company's platform is called Affilka
  'affilka': 'Affilka',
  'SOFTSWISS': 'Affilka',  // Handle uppercase variant too
  'hasoffers': 'HasOffers',
  'trackier': 'Trackier',
  'tune': 'TUNE',
  'everflow': 'Everflow',
  'affise': 'Affise',
  'cake': 'CAKE',
  'scaleo': 'Scaleo',
  'hitpath': 'Hitpath',
  'post affiliate pro': 'Post Affiliate Pro',
  'linktrust': 'LinkTrust',
  'affiliaxe': 'Affiliaxe',
  'tradedoubler': 'TradeDoubler',
  'ego': 'EGO',
  'custom': 'Custom',
  'map': 'MAP',
  'verajohn': 'VeraJohn',
  'v.partners': 'V.Partners',
  'topaffiliation': 'TopAffiliation',
  'uffiliates': 'Uffiliates',
  'intelitics': 'Intelitics',
  '1xbet': '1xBET',
  'irev': 'iRev',
  'growe': 'Growe',
  'omarsys': 'Omarsys',
  'mr.bet': 'Mr.Bet',
  'poshfriends': 'Poshfriends',
  'raventrack': 'RavenTrack',
  'livepartners': 'LivePartners',
  'q': 'Q',
};

function normalizePlatformName(name: string | null | undefined): string | null {
  if (!name) return null;
  const normalized = name.toLowerCase().trim();
  return PLATFORM_NAME_MAP[normalized] || name;
}

/**
 * Get auth token from localStorage
 */
function getAuthToken(): string | null {
  try {
    const auth = localStorage.getItem('adwstats-auth');
    if (auth) {
      const parsed = JSON.parse(auth);
      return parsed.state?.token || null;
    }
  } catch {
    return null;
  }
  return null;
}

/**
 * Fetch available networks (affiliate programs) from onlybrands-db
 */
export function useAvailableNetworks() {
  return useQuery({
    queryKey: ['availableNetworks'],
    queryFn: async (): Promise<Array<{
      id: string;
      name: string;
      logo: string | null;
      type: string;
      status: string;
      platform_provider: {
        id: string;
        name: string;
        logo: string | null;
      };
    }>> => {
      // Get fresh token at query execution time
      const token = getAuthToken();
      
      const response = await fetch(`${API_BASE}/networks/available`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch available networks');
      }

      const json = await response.json() as { data?: Array<{
        id: string;
        name: string;
        logo: string | null;
        type: string;
        status: string;
        platform_provider: {
          id: string;
          name: string;
          logo: string | null;
        };
      }> };
      return json.data || [];
    },
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });
}

/**
 * Fetch all platform providers from onlybrands-db
 */
export function usePlatformProviders() {
  return useQuery({
    queryKey: ['platformProviders'],
    queryFn: async (): Promise<PlatformProvider[]> => {
      // Get fresh token at query execution time
      const token = getAuthToken();
      
      const response = await fetch(`${API_BASE}/platforms`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch platform providers');
      }

      const result: any = await response.json();
      return result.data || [];
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000
  });
}

/**
 * Extract unique platform providers from networks data
 * This provides a dynamic list based on actual imported accounts
 * Includes count of accounts per platform and sorts by count (descending)
 */
export function useNetworkPlatformProviders() {
  const { data: networks } = useNetworks();
  
  const providers = React.useMemo(() => {
    if (!networks || networks.length === 0) return [];
    
    // Extract unique platform providers with normalization and count
    // Use a Map to dedupe by lowercase key while counting occurrences
    const providerMap = new Map<string, { name: string; count: number }>();
    networks.forEach(network => {
      const provider = network.platform_provider;
      if (provider && provider !== 'Unknown') {
        const key = provider.toLowerCase().trim();
        const normalizedName = normalizePlatformName(provider) || provider;
        
        if (providerMap.has(key)) {
          providerMap.get(key)!.count++;
        } else {
          providerMap.set(key, { name: normalizedName, count: 1 });
        }
      }
    });
    
    // Sort by count descending (largest first), then alphabetically for ties
    return Array.from(providerMap.values())
      .sort((a, b) => {
        if (b.count !== a.count) return b.count - a.count; // Largest first
        return a.name.localeCompare(b.name); // Alphabetic for ties
      })
      .map(item => ({
        id: item.name.toLowerCase().replace(/\s+/g, '-'),
        name: item.name,
        count: item.count
      }));
  }, [networks]);
  
  return { data: providers, isLoading: !networks };
}

/**
 * Fetch all networks with filtering
 */
export function useNetworks(filters?: NetworkFilters) {
  return useQuery({
    queryKey: ['networks', filters],
    queryFn: async (): Promise<NetworkCardData[]> => {
      console.log('[useNetworks] Starting fetch...');
      try {
        // Get fresh token at query execution time
        const token = getAuthToken();
        console.log('[useNetworks] Token:', token ? 'present' : 'missing');
        
        // Fetch from real API
        const params = new URLSearchParams();
        if (filters?.status && filters.status !== 'all') {
          params.append('status', filters.status);
        }
        if (filters?.platform && filters.platform !== 'all') {
          params.append('platform_provider_id', filters.platform);
        }

        console.log('[useNetworks] Fetching from API...');
        const response = await fetch(`${API_BASE}/networks?${params.toString()}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        console.log('[useNetworks] Response status:', response.status);
        if (!response.ok) {
          const errorText = await response.text();
          console.error('[useNetworks] API error:', errorText);
          throw new Error('Failed to fetch networks');
        }

        const result: any = await response.json();
        console.log('[useNetworks] Got', result.data?.length || 0, 'networks from API');
        const networks: AffiliateNetwork[] = result.data || [];

      // Transform to NetworkCardData
      let networkCards: NetworkCardData[] = networks.map(network => {
        const lastSyncFormatted = network.last_sync_at
          ? formatDistanceToNow(new Date(network.last_sync_at), { addSuffix: true })
          : 'Never';

        // Use default values for balance if not available
        const balanceCurrency = network.balance_currency || 'EUR';
        const currentBalance = network.current_balance || '0';
        const balanceFormatted = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: balanceCurrency,
          minimumFractionDigits: 2
        }).format(parseFloat(currentBalance));

        // Use last_sync_status instead of sync_status
        const syncStatus = (
          (network.last_sync_status === 'failed' ? 'error' : network.last_sync_status) || 
          network.sync_status || 
          'idle'
        ) as 'success' | 'partial' | 'running' | 'error' | 'never' | 'idle';
        
        let statusColor: 'green' | 'yellow' | 'red' | 'gray';
        let statusLabel: string;

        switch (syncStatus) {
          case 'success':
            statusColor = 'green';
            statusLabel = 'Success';
            break;
          case 'partial':
            statusColor = 'yellow';
            statusLabel = 'Partial';
            break;
          case 'running':
            statusColor = 'yellow';
            statusLabel = 'Syncing...';
            break;
          case 'error':
            statusColor = 'red';
            statusLabel = 'Error';
            break;
          case 'idle':
          case 'never':
          default:
            statusColor = 'gray';
            statusLabel = 'Never Synced';
        }

        // Add platform_name from network relation (advertiser/network name)
        const platformName = network.network?.name || network.platform_name || 'Unknown';
        
        // Platform provider - always normalize to ensure consistent casing
        // Parse metadata if it's a string
        const metadata = typeof network.metadata === 'string' 
          ? JSON.parse(network.metadata || '{}') 
          : (network.metadata || {});
        
        // Get raw platform provider from various sources
        let rawProvider = network.platform_provider || metadata.affiliate_system_name;
        
        // If no platform provider found, try to detect from login URL
        const loginUrlToCheck = network.login_url || metadata.login_url;
        if (!rawProvider && loginUrlToCheck) {
          const loginUrl = loginUrlToCheck.toLowerCase();
          if (loginUrl.includes('netrefer') || (loginUrl.includes('affiliate.') && loginUrl.includes('.com'))) {
            rawProvider = 'NetRefer';
          } else if (loginUrl.includes('incomeaccess')) {
            rawProvider = 'IncomeAccess';
          } else if (loginUrl.includes('hasoffers')) {
            rawProvider = 'HasOffers';
          } else if (loginUrl.includes('myaffiliates')) {
            rawProvider = 'MyAffiliates';
          } else if (loginUrl.includes('referon')) {
            rawProvider = 'ReferOn';
          } else if (loginUrl.includes('cellxpert')) {
            rawProvider = 'Cellxpert';
          } else if (loginUrl.includes('softswiss') || loginUrl.includes('affilka')) {
            rawProvider = 'Affilka';  // SOFTSWISS company's affiliate platform is called Affilka
          }
        }
        
        // Always normalize the platform provider to ensure consistent casing
        // Also handle if the API already normalized SOFTSWISS to Affilka
        const platformProvider = normalizePlatformName(rawProvider) || 
          (rawProvider === 'SOFTSWISS' ? 'Affilka' : rawProvider);
        
        // Determine platform type from platform provider or network.platform_type
        const platformType = platformProvider || network.platform_type;

        // Get summary data from inline API response (no separate request needed)
        const summary = (network as any).summary || {
          months_synced: 0,
          total_players: 0,
          total_ngr: 0,
          total_commission: 0,
          current_balance: 0,
          first_sync_date: null,
          last_sync_date: null,
          has_data: false
        };

        return { 
          ...network, 
          platform_name: platformName,
          platform_provider: platformProvider,
          platform_type: platformType,
          tags: network.tags || [],
          balance_currency: balanceCurrency,
          current_balance: currentBalance,
          sync_status: syncStatus,
          sync_error_message: network.last_sync_error || null,
          lastSyncFormatted, 
          balanceFormatted, 
          statusColor, 
          statusLabel,
          // Inline summary data - no separate API call needed
          summary
        };
      });

      // Apply client-side filters
      if (filters) {
        // Search filter - check multiple fields
        if (filters.search) {
          const search = filters.search.toLowerCase().trim();
          networkCards = networkCards.filter(n => 
            n.account_name?.toLowerCase().includes(search) ||
            n.platform_name?.toLowerCase().includes(search) ||
            n.username?.toLowerCase().includes(search) ||
            n.network?.name?.toLowerCase().includes(search) ||
            n.id?.toLowerCase().includes(search) ||
            n.network_id?.toLowerCase().includes(search) ||
            n.tags?.some((tag: string) => tag.toLowerCase().includes(search)) ||
            n.metadata?.advertiser_name?.toLowerCase().includes(search) ||
            n.metadata?.affiliate_system_name?.toLowerCase().includes(search)
          );
        }

        // Status filter (client-side)
        if (filters.status && filters.status !== 'all') {
          networkCards = networkCards.filter(n => n.sync_status === filters.status);
        }

        // Platform filter (client-side) - matches on platform_provider (affiliate system)
        if (filters.platform && filters.platform !== 'all') {
          const filterValue = filters.platform.toLowerCase().replace(/\s+/g, '-');
          networkCards = networkCards.filter(n => {
            // Get provider from multiple sources and normalize
            let provider = n.platform_provider;
            
            // Parse metadata if it's a string
            const parsedMetadata = typeof n.metadata === 'string' 
              ? JSON.parse(n.metadata || '{}') 
              : (n.metadata || {});
            
            // Fallback: Check metadata.affiliate_system_name
            if (!provider && parsedMetadata.affiliate_system_name) {
              provider = normalizePlatformName(parsedMetadata.affiliate_system_name);
            }
            
            // Fallback: Check login URL for direct accounts
            if (!provider) {
              const loginUrl = (parsedMetadata.login_url || '').toLowerCase();
              if (loginUrl.includes('netrefer') || (loginUrl.includes('affiliate.') && loginUrl.includes('.com'))) {
                provider = 'NetRefer';
              } else if (loginUrl.includes('incomeaccess')) {
                provider = 'IncomeAccess';
              } else if (loginUrl.includes('myaffiliates')) {
                provider = 'MyAffiliates';
              } else if (loginUrl.includes('referon')) {
                provider = 'ReferOn';
              } else if (loginUrl.includes('cellxpert')) {
                provider = 'Cellxpert';
              } else if (loginUrl.includes('softswiss') || loginUrl.includes('affilka')) {
                provider = 'Affilka';  // SOFTSWISS company's affiliate platform is called Affilka
              }
            }
            
            if (!provider) return false;
            
            // Compare lowercase versions to be case-insensitive
            const providerNormalized = provider.toLowerCase().replace(/\s+/g, '-');
            return providerNormalized === filterValue;
          });
        }

        // Sorting
        networkCards.sort((a, b) => {
          let aVal: any, bVal: any;
          switch (filters.sortBy) {
            case 'name': aVal = a.account_name || ''; bVal = b.account_name || ''; break;
            case 'balance': aVal = parseFloat(a.current_balance || '0'); bVal = parseFloat(b.current_balance || '0'); break;
            case 'last_sync': aVal = a.last_sync_at ? new Date(a.last_sync_at).getTime() : 0; bVal = b.last_sync_at ? new Date(b.last_sync_at).getTime() : 0; break;
            case 'status': aVal = a.sync_status || ''; bVal = b.sync_status || ''; break;
            default: return 0;
          }
          if (aVal < bVal) return filters.sortOrder === 'asc' ? -1 : 1;
          if (aVal > bVal) return filters.sortOrder === 'asc' ? 1 : -1;
          return 0;
        });
      }

      console.log('[useNetworks] Returning', networkCards.length, 'network cards');
      return networkCards;
      } catch (error) {
        // Only log once, not for every retry
        if (!(error instanceof Error && error.message === 'Failed to fetch')) {
          console.error('[useNetworks] Error in queryFn:', error);
        }
        throw error;
      }
    },
    staleTime: 60 * 1000, // Cache for 1 minute
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false, // Disable automatic refetch on tab focus
    retry: 1, // Only retry once on failure
  });
}

export function useCreateNetwork() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: NewNetworkData) => {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE}/networks`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error('Failed to create network');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['networks'] });
    }
  });
}

// Get stored Affiliation Cloud token for 2FA (if available)
function getACToken(): string | null {
  try {
    const stored = localStorage.getItem('affiliation-cloud-token');
    if (stored) {
      const parsed = JSON.parse(stored);
      // Check if not expired
      if (parsed.expiresAt > Date.now()) {
        return parsed.token;
      }
    }
  } catch {
    // Ignore
  }
  return null;
}

export function useSyncNetwork() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (networkId: string) => {
      const token = getAuthToken();
      const acToken = getACToken(); // Get AC token for 2FA if available
      
      // Step 1: Create sync job
      const syncResponse = await fetch(`${API_BASE}/networks/${networkId}/sync`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!syncResponse.ok) throw new Error('Failed to create sync job');
      const syncResult = await syncResponse.json() as { data?: { job_id?: string } };
      
      console.log('Sync job created:', syncResult);
      
      // Get job_id from sync response
      const jobId = syncResult.data?.job_id;
      if (!jobId) {
        throw new Error('Sync job created but no job_id returned');
      }
      
      // Step 2: Process the sync job immediately (MVP testing mode)
      // Include AC token header if available (used for fetching 2FA OTP)
      const headers: Record<string, string> = {
        'Authorization': `Bearer ${token}`
      };
      if (acToken) {
        headers['X-Affiliation-Cloud-Token'] = acToken;
      }
      
      const processResponse = await fetch(`${API_BASE}/networks/${networkId}/process-sync?job_id=${jobId}`, {
        method: 'POST',
        headers
      });
      if (!processResponse.ok) {
        const errorData = await processResponse.json() as { 
          message?: string; 
          error?: string;
          result?: { error?: string };
          data?: { error?: string; status?: string };
        };
        // Extract error from various possible locations in the response
        const errorMessage = errorData.data?.error || errorData.result?.error || errorData.error || errorData.message || 'Failed to process sync';
        console.error('Sync error response:', JSON.stringify(errorData, null, 2));
        throw new Error(errorMessage);
      }
      const processResult = await processResponse.json();
      
      console.log('Sync processed:', processResult);
      
      return processResult;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['networks'] });
    }
  });
}

/**
 * Historical sync result type
 */
interface HistoricalSyncResult {
  success: boolean;
  message?: string;
  error?: string;
  summary?: {
    total_months_requested: number;
    months_processed_this_request: number;
    has_more_months: boolean;
    remaining_months: number;
    months_already_synced: number;
    months_synced_now: number;
    months_empty: number;
    months_failed: number;
    total_records_inserted: number;
    total_insert_errors: number;
    total_players_synced: number;
    player_sync_error?: string;
    player_debug?: Record<string, unknown>;
    duration_ms: number;
    note?: string;
    // Additional fields from backend
    first_data_month?: string;
    hit_account_start_boundary?: boolean;
    months_skipped_before_account_start?: number;
  };
  details?: Array<{
    month: string;
    status: string;
    records?: number;
    error?: string;
  }>;
}

/**
 * Historical sync - fetch all data from 2023-01-01 month by month
 * Auto-continues until all months are synced (max 20 iterations to prevent infinite loops)
 */
export function useHistoricalSync() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ networkId, startDate = '2019-01-01', force = false, skipPlayers = true, skipStats = false }: { 
      networkId: string; 
      startDate?: string; 
      force?: boolean;
      skipPlayers?: boolean; // Skip player sync to reduce subrequests (default: true for historical sync)
      skipStats?: boolean; // Skip stats sync (for player-only sync)
    }): Promise<HistoricalSyncResult> => {
      const token = getAuthToken();
      
      // Build base params with offset support
      const buildParams = (currentOffset: number = 0) => {
        const params = new URLSearchParams();
        // When force=true, don't send start_date - let backend use its default (2019-01-01)
        // This ensures force resync fetches ALL historical data
        if (!force) {
          params.append('start_date', startDate);
        }
        if (force) params.append('force', 'true');
        if (skipPlayers) params.append('skip_players', 'true');
        if (skipStats) params.append('skip_stats', 'true');
        if (currentOffset > 0) params.append('offset', currentOffset.toString());
        return params;
      };
      
      let combinedResult: HistoricalSyncResult | null = null;
      let iterations = 0;
      let currentOffset = 0; // Track offset for force sync pagination
      const MAX_ITERATIONS = 20; // Prevent infinite loops (20 iterations * 6 months = 120 months max)
      
      // Keep fetching until no more months remain
      while (iterations < MAX_ITERATIONS) {
        iterations++;
        console.log(`[Historical sync] Iteration ${iterations} (offset=${currentOffset})...`);
        
        const response = await fetch(`${API_BASE}/networks/${networkId}/historical-sync?${buildParams(currentOffset).toString()}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          const errorData = await response.json() as { 
            message?: string; 
            error?: string; 
            debugSteps?: string[];
            lastStep?: string;
            errorName?: string;
          };
          console.error('Historical sync API error:', errorData);
          if (errorData.debugSteps) {
            console.error('Debug steps:', errorData.debugSteps);
            console.error('Last step:', errorData.lastStep);
          }
          const errorMessage = errorData.message || errorData.error || 'Failed to start historical sync';
          throw new Error(`${errorMessage}${errorData.lastStep ? ` (last step: ${errorData.lastStep})` : ''}`);
        }
        
        const result = await response.json() as HistoricalSyncResult;
        console.log(`[Historical sync] Iteration ${iterations} result:`, result.summary);
        
        // Combine results
        if (!combinedResult) {
          combinedResult = result;
        } else if (combinedResult.summary && result.summary) {
          // Merge the summaries - accumulate counts
          combinedResult.summary.months_synced_now = (combinedResult.summary.months_synced_now || 0) + (result.summary.months_synced_now || 0);
          combinedResult.summary.total_records_inserted = (combinedResult.summary.total_records_inserted || 0) + (result.summary.total_records_inserted || 0);
          combinedResult.summary.total_players_synced = (combinedResult.summary.total_players_synced || 0) + (result.summary.total_players_synced || 0);
          combinedResult.summary.months_failed = (combinedResult.summary.months_failed || 0) + (result.summary.months_failed || 0);
          // Use the LATEST iteration's first_data_month (most accurate after all months processed)
          if (result.summary.first_data_month) {
            combinedResult.summary.first_data_month = result.summary.first_data_month;
          }
          // Update other fields from latest iteration
          combinedResult.summary.total_months_requested = result.summary.total_months_requested;
          combinedResult.summary.hit_account_start_boundary = result.summary.hit_account_start_boundary;
          combinedResult.details = [...(combinedResult.details || []), ...(result.details || [])];
        }
        
        // Check if there are more months to sync
        if (!result.summary?.has_more_months || result.summary.remaining_months === 0) {
          console.log(`[Historical sync] Complete after ${iterations} iterations`);
          break;
        }
        
        // Update offset for next iteration (use next_offset from API or increment manually)
        const nextOffset = (result.summary as any)?.next_offset;
        if (typeof nextOffset === 'number' && nextOffset > currentOffset) {
          currentOffset = nextOffset;
        } else {
          // Fallback: increment by 6 (default batch size)
          currentOffset += 6;
        }
        
        // Small delay between iterations to avoid overwhelming the server
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
      if (iterations >= MAX_ITERATIONS && combinedResult?.summary?.has_more_months) {
        console.warn(`[Historical sync] Reached max iterations (${MAX_ITERATIONS}), some months may not be synced`);
        combinedResult.summary.note = `Sync stopped after ${MAX_ITERATIONS} iterations. Run again to continue.`;
      }
      
      return combinedResult!;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['networks'] });
      queryClient.invalidateQueries({ queryKey: ['statistics'] });
    }
  });
}

export function useTestConnection() {
  return useMutation({
    mutationFn: async (data: Step2Data): Promise<TestConnectionResult> => {
      // Get fresh token at query execution time
      const token = getAuthToken();
      
      const response = await fetch(`${API_BASE}/networks/test-connection`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const error: any = await response.json().catch(() => ({ error: 'Connection test failed' }));
        throw new Error(error.error || 'Failed to test connection');
      }

      const result: any = await response.json();
      return result.data || result;
    }
  });
}

export function useDeleteNetwork() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (networkId: string) => {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE}/networks/${networkId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) throw new Error('Failed to delete network');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['networks'] });
    }
  });
}

/**
 * Update network settings (name, API key, etc.)
 */
export function useUpdateNetwork() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ networkId, updates }: { 
      networkId: string; 
      updates: { 
        account_name?: string; 
        username?: string; 
        email?: string; 
        password?: string; 
        api_key?: string; 
        tags?: string[];
        requires_2fa?: boolean | null;
        currency?: string;
        tracker_check?: boolean;
      } 
    }) => {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE}/networks/${networkId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Update failed' })) as { error?: string; message?: string };
        throw new Error(errorData.error || errorData.message || 'Failed to update network');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['networks'] });
    }
  });
}

export interface TrackerCheckResult {
  success: boolean;
  error?: string;
  platform?: string;
  network?: string;
  players?: number;
  matched?: number;
  filled?: { reg: number; ftd: number };
  alreadyRecorded?: number;
  unknownClickIds?: string[];
  filledConversions?: Array<{ clickId: string; playerId: string; event: 'reg' | 'ftd'; date: string }>;
}

/**
 * Tracker check: fill missing tracker postbacks from the account's player reports
 */
export function useTrackerCheck() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ networkId, days }: { networkId: string; days?: number }): Promise<TrackerCheckResult> => {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE}/networks/${networkId}/tracker-check`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ days })
      });
      const result = await response.json().catch(() => ({ success: false, error: `HTTP ${response.status}` })) as TrackerCheckResult;
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Tracker check failed');
      }
      return result;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['networks'] });
    }
  });
}

/**
 * Fetch summary stats for a single network (months synced, players, commission)
 * 
 * DISABLED: Making 354 simultaneous requests crashes the worker.
 * Summary data should be included in the main /api/networks endpoint instead.
 */
export function useNetworkSummary(networkId: string) {
  return useQuery({
    queryKey: ['networkSummary', networkId],
    queryFn: async (): Promise<NetworkSummary> => {
      // Return empty summary - the actual data should come from the main networks endpoint
      return {
        months_synced: 0,
        total_players: 0,
        total_ngr: 0,
        total_commission: 0,
        current_balance: 0,
        first_sync_date: null,
        last_sync_date: null,
        has_data: false
      };
    },
    enabled: false, // DISABLED - too many simultaneous requests crash the worker
    staleTime: 60000, // Cache for 1 minute
  });
}

/**
 * Fetch detailed account info including login_url (for Edit modal)
 */
export interface NetworkDetails {
  id: string;
  account_name: string;
  account_identifier: string;
  username: string;
  email?: string;     // For platforms like ReferOn that require email login
  has_email?: boolean;
  password?: string;  // For debugging - exposed from details API
  api_key?: string;   // For debugging - exposed from details API
  has_password: boolean;
  has_api_key: boolean;
  login_url: string | null;
  sync_from_date: string | null;
  affiliate_system_name: string | null;
  original_network_name: string | null;
  network_id: string;
  is_active: boolean;
  sync_enabled: boolean;
  last_sync_at: string | null;
  sync_frequency_minutes: number;
  tags: string[];
  metadata_raw?: Record<string, unknown>;
}

export function useNetworkDetails(networkId: string) {
  return useQuery({
    queryKey: ['networkDetails', networkId],
    queryFn: async (): Promise<NetworkDetails> => {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE}/networks/${networkId}/details`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch network details');
      }
      
      const result = await response.json() as { success: boolean; data: NetworkDetails };
      return result.data;
    },
    enabled: !!networkId,
    staleTime: 30000, // Cache for 30 seconds
  });
}

type Step2Data = { credentials: Record<string, string>; login_url: string; username?: string };
